import "./App.css";
import { useState, useEffect } from "react";
import { db } from "../src/config/firestore";
import {
  getDocs,
  query,
  where,
  collection,
} from "firebase/firestore";
import AdventCalendar from "./components/AdventCalendar";
import DayLeaderboard from "./components/DayLeaderboard";
import DailyChallenge from "./components/DailyChallenge";
import {
  getUserProgress,
  updateDayScore,
  initializeUserProgress,
} from "./config/adventUtils";

function App() {
  const [user, setUser] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [view, setView] = useState("calendar"); // 'calendar', 'dayLeaderboard', 'challenge'
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);

  // Restore user session on app load
  useEffect(() => {
    const restoreSession = async () => {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        await loadUserProgress(parsedUser.uid);
      }
      setIsLoadingSession(false);
    };
    restoreSession();
  }, []);

  const loadUserProgress = async (userId) => {
    const progress = await getUserProgress(userId);
    setUserProgress(progress);
  };

  // Handle login using the invite key
  const handleLogin = async (inviteKey) => {
    try {
      const q = query(
        collection(db, "invites"),
        where("inviteKey", "==", inviteKey)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const userData = doc.data();
        const loggedInUser = { uid: doc.id, name: userData.name };
        setUser(loggedInUser);

        // Initialize user progress with their name
        await initializeUserProgress(doc.id, userData.name);
        await loadUserProgress(doc.id);

        localStorage.setItem("user", JSON.stringify(loggedInUser));
      } else {
        alert("Ugyldig invitasjonsnøkkel. Vennligst prøv igjen.");
      }
    } catch (error) {
      console.error("Error during login:", error);
      alert("Noe gikk galt. Vennligst prøv igjen.");
    }
  };

  const handleDaySelect = (challenge) => {
    setSelectedChallenge(challenge);
    setView("dayLeaderboard");
  };

  const handleStartChallenge = () => {
    setView("challenge");
  };

  const handleChallengeComplete = async (day, score, guessCoords) => {
    // Update score in Firestore
    await updateDayScore(user.uid, day, score, user.name, guessCoords);

    // Reload user progress
    await loadUserProgress(user.uid);

    // Return to calendar
    setView("calendar");
    setSelectedChallenge(null);
  };

  const handleBackToCalendar = () => {
    setView("calendar");
    setSelectedChallenge(null);
  };

  if (isLoadingSession) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-advent-primary-dark to-advent-secondary-dark">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white mb-4"></div>
        <div className="text-white text-2xl">Laster...</div>
      </div>
    );
  }

  return (
    <>
      {view === "calendar" && (
        <AdventCalendar
          user={user}
          userProgress={userProgress}
          onDaySelect={handleDaySelect}
          onLogin={handleLogin}
        />
      )}
      {view === "dayLeaderboard" && selectedChallenge && user && (
        <DayLeaderboard
          challenge={selectedChallenge}
          user={user}
          userProgress={userProgress}
          onStart={handleStartChallenge}
          onBack={handleBackToCalendar}
        />
      )}
      {view === "challenge" && selectedChallenge && user && (
        <DailyChallenge
          challenge={selectedChallenge}
          user={user}
          onComplete={handleChallengeComplete}
          onBack={handleBackToCalendar}
        />
      )}
    </>
  );
}

export default App;
